import type { ReactNode } from 'react';

type BadgeSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'success';
type BadgeSize = 'sm' | 'md';
type BadgeCorners = 'asymmetric' | 'asymmetric-inverse' | 'rounded' | 'sharp';

interface BadgeProps {
  severity?: BadgeSeverity;
  size?: BadgeSize;
  corners?: BadgeCorners;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

const severityStyles: Record<BadgeSeverity, string> = {
  critical: 'bg-primary text-text-inverse border-primary',
  high: 'bg-surface border-primary text-text-primary',
  medium: 'bg-surface border-border-light text-text-secondary',
  low: 'bg-surface border-border text-text-muted',
  info: 'bg-surface border-border text-text-secondary',
  success: 'bg-primary text-text-inverse border-primary',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-sm px-3 py-1 gap-1.5',
};

const cornerStyles: Record<BadgeCorners, string> = {
  'asymmetric': 'rounded-asymmetric-sm',
  'asymmetric-inverse': 'rounded-asymmetric-inverse-sm',
  'rounded': 'rounded-md',
  'sharp': 'rounded-none',
};

export function Badge({
  severity = 'info',
  size = 'md',
  corners = 'asymmetric',
  icon,
  children,
  className = '',
  onClick,
}: BadgeProps) {
  return (
    <span
      onClick={onClick}
      className={`
        inline-flex items-center border font-mono font-semibold uppercase tracking-wider
        ${cornerStyles[corners]}
        ${severityStyles[severity]}
        ${sizeStyles[size]}
        ${onClick ? 'cursor-pointer hover:bg-surface-hover transition-colors' : ''}
        ${className}
      `}
    >
      {icon}
      {children}
    </span>
  );
}
